import { PricePromise } from "../PricePromise";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { TOK, SERIF, SANS } from "../../pages/Soulumo/SignatureBits";
import { openPaddleIfActive } from "./paddleCheckout";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });

// Paywall for non-entitled users + post-checkout confirmation polling.
export const CloneSubscribe = ({ onActivated }) => {
  const [params, setParams] = useSearchParams();
  const sid = params.get("session_id");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("monthly");
  const [msg, setMsg] = useState(params.get("cancelled") ? "Checkout cancelled — no charge was made." : "");

  useEffect(() => {
    if (!sid) return undefined;
    let n = 0;
    setMsg("Confirming your subscription…");
    const t = setInterval(async () => {
      n += 1;
      try {
        const r = await axios.get(`${API}/api/voice-clone/checkout/status/${sid}`, auth());
        if (r.data.payment_status === "paid") {
          clearInterval(t);
          setParams({});
          setMsg("");
          onActivated();
        }
      } catch { /* keep polling */ }
      if (n >= 20) { clearInterval(t); setMsg("Payment is still processing — refresh in a minute."); }
    }, 2000);
    return () => clearInterval(t);
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!params.get("paddle")) return undefined;
    let n = 0;
    setMsg("Confirming your membership…");
    const t = setInterval(async () => {
      n += 1;
      try {
        const r = await axios.get(`${API}/api/voice-clone/me`, auth());
        if (r.data.enabled) { clearInterval(t); setParams({}); setMsg(""); onActivated(); }
      } catch { /* keep polling */ }
      if (n >= 30) { clearInterval(t); setMsg("Payment is still processing. Refresh in a minute."); }
    }, 2000);
    return () => clearInterval(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async () => {
    setBusy(true);
    try {
      if (await openPaddleIfActive({ plan, source: "voice_clone_page" })) { setBusy(false); return; }
      const r = await axios.post(`${API}/api/voice-clone/checkout`, { origin_url: window.location.origin, plan }, auth());
      window.location.href = r.data.checkout_url;
    } catch {
      setMsg("We couldn't open checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="rounded-3xl px-6 py-7" style={{ border: `1px solid ${TOK.gold}55`, background: "rgba(215,187,122,0.05)" }} data-testid="clone-subscribe">
      <div className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.3em] mb-3" style={{ color: TOK.gold, fontFamily: SANS }}>
        <Lock size={11} /> Premium add-on
      </div>
      <div className="grid grid-cols-2 gap-3" data-testid="clone-plans">
        <PlanCard active={plan === "monthly"} onClick={() => setPlan("monthly")} testId="clone-plan-monthly"
          tag="Founding member" price="£6.99" was="£9.99" per="/ month · about one fancy coffee" />
        <PlanCard active={plan === "annual"} onClick={() => setPlan("annual")} testId="clone-plan-annual"
          tag="Annual" price="£59.99" per="/ year" badge="Over 3 months free" />
      </div>
      <p className="mt-3 italic" style={{ color: TOK.creamDim, fontFamily: SERIF }}>Your own voice in every new installation. Cancel any time.</p>
      <PricePromise className="mt-4" />
      <button type="button" onClick={subscribe} disabled={busy || !!sid} data-testid="clone-subscribe-btn"
        className="mt-5 inline-flex items-center gap-2 rounded-full px-7 py-3 text-[0.7rem] uppercase tracking-[0.22em] disabled:opacity-50"
        style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS, minHeight: 48 }}>
        {(busy || sid) && <Loader2 size={13} className="animate-spin" />} Unlock My voice
      </button>
      {msg && <p className="mt-3 text-sm" style={{ color: TOK.creamDim }} data-testid="clone-subscribe-msg">{msg}</p>}
    </div>
  );
};

const PlanCard = ({ active, onClick, tag, price, was, per, badge, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className="rounded-2xl p-4 text-left transition-colors duration-200"
    style={{ border: `${active ? 2 : 1}px solid ${active ? TOK.gold : `${TOK.gold}33`}`, background: active ? "rgba(215,187,122,0.16)" : "transparent" }}>
    <div className="text-[0.58rem] uppercase tracking-[0.24em]" style={{ color: TOK.gold, fontFamily: SANS }}>{tag}</div>
    {badge && <span className="mt-1.5 inline-block rounded-full px-2 py-0.5 text-[0.55rem] uppercase font-bold tracking-[0.14em]" style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS }} data-testid={`${testId}-badge`}>{badge}</span>}
    <div className="mt-1 text-2xl font-light" style={{ fontFamily: SERIF, color: TOK.cream }} data-testid={`${testId}-price`}>
      {was && <s className="text-base mr-1.5" style={{ color: TOK.creamMist }}>{was}</s>}{price}
    </div>
    <div className="text-xs" style={{ color: TOK.creamMist, fontFamily: SANS }}>{per}</div>
  </button>
);

export const ManageBilling = () => {
  const open = async () => {
    const r = await axios.post(`${API}/api/voice-clone/portal`, { origin_url: window.location.origin }, auth());
    window.location.href = r.data.url;
  };
  return (
    <button type="button" onClick={open} data-testid="clone-manage-billing-btn" className="text-xs underline" style={{ color: TOK.creamMist }}>
      Manage subscription
    </button>
  );
};

const PERKS = ["Our most faithful voice: sounds closest to you", "20 installations a month, plus 2 to make for someone you love", "Re-make older installations in your voice, free", "Your installations build first"];

// My Voice Premium upsell, and post-checkout confirmation for upgrades.
export const PremiumCard = ({ onActivated, hasPlan }) => {
  const [params, setParams] = useSearchParams();
  const sid = params.get("session_id");
  const [plan, setPlan] = useState("premium_monthly");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!sid) return undefined;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try {
        const r = await axios.get(`${API}/api/voice-clone/checkout/status/${sid}`, auth());
        if (r.data.payment_status === "paid") { clearInterval(t); setParams({}); onActivated(); }
      } catch { /* keep polling */ }
      if (n >= 20) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  const [open, setOpen] = useState(!!sid);
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/voice-clone/checkout`, { origin_url: window.location.origin, plan }, auth());
      if (r.data.upgraded) { setBusy(false); setMsg("You're on Premium. You only paid the difference for the rest of this period."); onActivated(); return; }
      window.location.href = r.data.checkout_url;
    } catch (e) { setMsg(e?.response?.status === 402 ? "Your card was declined. Please update it in Manage subscription." : "We couldn't open checkout. Please try again."); setBusy(false); }
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid="clone-premium-open"
        className="mt-10 text-sm underline-offset-4 hover:underline" style={{ color: TOK.creamMist, fontFamily: SANS }}>
        ✦ My Voice Premium · our most faithful voice, 20 a month and gifts
      </button>
    );
  }
  return (
    <div className="mt-10 rounded-3xl px-6 py-7" style={{ border: `1px solid ${TOK.gold}`, background: "linear-gradient(160deg, rgba(215,187,122,0.14), rgba(215,187,122,0.03))" }} data-testid="clone-premium">
      <div className="text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: TOK.gold, fontFamily: SANS }}>My Voice Premium</div>
      <p className="mt-2 text-2xl font-light" style={{ fontFamily: SERIF, color: TOK.cream }}>Sound even more like you.</p>
      <ul className="mt-4 space-y-2">
        {PERKS.map((p) => <li key={p} className="text-sm flex gap-2" style={{ color: TOK.creamDim, fontFamily: SANS }}><span style={{ color: TOK.gold }}>✦</span>{p}</li>)}
      </ul>
      <div className="mt-5 grid grid-cols-2 gap-3">
        <PlanCard active={plan === "premium_monthly"} onClick={() => setPlan("premium_monthly")} testId="clone-plan-premium-monthly" tag="Monthly" price="£12.99" per="/ month" />
        <PlanCard active={plan === "premium_annual"} onClick={() => setPlan("premium_annual")} testId="clone-plan-premium-annual" tag="Yearly" price="£119.99" per="/ year" badge="2 months free" />
      </div>
      <button type="button" onClick={go} disabled={busy || !!sid} data-testid="clone-premium-btn"
        className="mt-5 inline-flex items-center gap-2 rounded-full px-7 py-3 text-[0.7rem] uppercase tracking-[0.22em] disabled:opacity-50"
        style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS, minHeight: 48 }}>
        {(busy || sid) && <Loader2 size={13} className="animate-spin" />} Go Premium
      </button>
      <p className="mt-3 text-xs" style={{ color: TOK.creamMist }}>{hasPlan ? "Replaces your current My Voice plan. " : ""}Cancel any time.</p>
      {msg && <p className="mt-2 text-sm" style={{ color: TOK.creamDim }} data-testid="clone-premium-msg">{msg}</p>}
    </div>
  );
};

export const PremiumBadge = () => (
  <div className="mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.6rem] uppercase tracking-[0.26em]" style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS }} data-testid="clone-premium-badge">
    ✦ Premium · most faithful voice · 20 a month + 2 to gift
  </div>
);
